import type { AppSession, SessionProfile } from '../../shared/types/domain';
import { getSupabaseClient } from '../../shared/api/supabase';

async function resolveSession(): Promise<AppSession | null> {
  const supabase = getSupabaseClient();
  const { data: { session }, error } = await supabase.auth.getSession();
  if (error) throw error;
  if (!session) return null;
  const { data: profile, error: profileError } = await supabase
    .from('profiles')
    .select('id,phone,name,role,status,store_id,stores(name)')
    .eq('id', session.user.id)
    .single();
  if (profileError) throw profileError;
  const row = profile as unknown as {
    id: string; phone: string; name: string; role: SessionProfile['role'];
    status: SessionProfile['status']; store_id: string | null;
    stores: { name?: string } | { name?: string }[] | null;
  };
  if (row.status !== 'active') throw new Error('账号尚未启用或已被停用。');
  const store = Array.isArray(row.stores) ? row.stores[0] : row.stores;
  const normalized: SessionProfile = {
    id: row.id,
    phone: row.phone,
    name: row.name,
    role: row.role,
    status: row.status,
    storeId: row.store_id,
    storeName: (store as { name?: string } | null)?.name ?? null,
  };
  return { accessToken: session.access_token, profile: normalized, expiresAt: session.expires_at ?? null, source: 'supabase' };
}

export const supabaseAuth = {
  restore: resolveSession,
  async signInWithPassword(phone: string, password: string) {
    const { error } = await getSupabaseClient().auth.signInWithPassword({ phone: `+86${phone}`, password });
    if (error) {
      if (error.message.toLowerCase().includes('invalid login credentials')) throw new Error('手机号或密码错误。');
      throw error;
    }
    const session = await resolveSession();
    if (!session) throw new Error('登录成功但没有找到有效账号档案。');
    return session;
  },
  async signInAsDemo(): Promise<AppSession> {
    throw new Error('Supabase模式不允许演示角色绕过认证。');
  },
  async signOut() {
    const { error } = await getSupabaseClient().auth.signOut({ scope: 'local' });
    if (error) throw error;
  },
};
